import React from 'react';
import { Alert, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';

import { getLumberDescription, LumberWithStockLengthAndPrice } from '@paragon/contract-library';
import { NumericField } from '@paragon/material';
import { naturalSortByProperty } from '@paragon/utilities';

interface MissingPricesProps {
  readonly requireNonZeroPrices: boolean;
  readonly setRequireNonZeroPrices: (require: boolean) => void;
  readonly userSpecifiedPlatePrices: { [plateType: string]: number };
  readonly userSpecifiedHangerPrices: { [hanger: string]: number };
  readonly userSpecifiedCustomHangerPrices: { [hanger: string]: number };
  readonly userSpecifiedLumberPrices: readonly LumberWithStockLengthAndPrice[];
  readonly setUserSpecifiedLumberPrice: (lumberWithStockLengthAndPrice: LumberWithStockLengthAndPrice) => void;
  readonly setUserSpecifiedPlatePrice: (plateType: string, price: number) => void;
  readonly setUserSpecifiedHangerPrice: (hanger: string, price: number) => void;
  readonly setUserSpecifiedCustomHangerPrice: (hanger: string, price: number) => void;
  readonly includeHangerPricesInEstimate: boolean;
  readonly setIncludeHangerPricesInEstimate: (includeHangerPricesInEstimate: boolean) => void;
  readonly distanceFormatter: (inches: number) => string;
  readonly submitAttemptedWithoutAllPrices: boolean;
}

export function Pricing({
  requireNonZeroPrices,
  setRequireNonZeroPrices,
  userSpecifiedLumberPrices,
  userSpecifiedPlatePrices,
  userSpecifiedHangerPrices,
  userSpecifiedCustomHangerPrices,
  setUserSpecifiedLumberPrice,
  setUserSpecifiedPlatePrice,
  setUserSpecifiedHangerPrice,
  setUserSpecifiedCustomHangerPrice,
  includeHangerPricesInEstimate,
  setIncludeHangerPricesInEstimate,
  distanceFormatter,
  submitAttemptedWithoutAllPrices,
}: MissingPricesProps) {
  const areMissingPrices =
    userSpecifiedLumberPrices.length !== 0 ||
    Object.keys(userSpecifiedPlatePrices).length !== 0 ||
    (includeHangerPricesInEstimate &&
      (Object.keys(userSpecifiedHangerPrices).length !== 0 ||
        Object.keys(userSpecifiedCustomHangerPrices).length !== 0));

  return (
    <Stack spacing={2}>
      <Typography variant="h5">Pricing</Typography>
      <FormControlLabel
        label="Include hanger prices"
        control={
          <Checkbox
            checked={includeHangerPricesInEstimate}
            onChange={(_, checked) => void setIncludeHangerPricesInEstimate(checked)}
          />
        }
      />
      {areMissingPrices && (
        <>
          <Typography variant="h6">Specify Missing Prices</Typography>
          <FormControlLabel
            label="Require non-zero prices"
            control={
              <Checkbox
                checked={requireNonZeroPrices}
                onChange={(_, checked) => void setRequireNonZeroPrices(checked)}
              />
            }
          />
          {submitAttemptedWithoutAllPrices ? <Alert severity="error">Some prices are not set</Alert> : null}
          {userSpecifiedLumberPrices.length > 0 && (
            <>
              <Typography variant="h6">Lumber</Typography>
              <Typography>
                You do not need to provide a price for every length lumber, only the longest one within a common
                dimension-species-grade-treatment combination.
              </Typography>
            </>
          )}
          {userSpecifiedLumberPrices.map(({ lumber, stockLengthInInches, price }) => (
            <NumericField
              key={`${getLumberDescription(lumber)} ${distanceFormatter(stockLengthInInches)}`}
              label={`${getLumberDescription(lumber)} ${distanceFormatter(stockLengthInInches)}`}
              startAdornment="$"
              endAdornment="/1,000 BF"
              min={0}
              numberOfDecimals={2}
              padWithZeros
              value={price}
              onSubmit={(newValue) =>
                void setUserSpecifiedLumberPrice({ lumber, stockLengthInInches, price: newValue })
              }
            />
          ))}
          {Object.keys(userSpecifiedPlatePrices).length > 0 && <Typography variant="h6">Plates</Typography>}
          {naturalSortByProperty(Object.keys(userSpecifiedPlatePrices), (plateType) => plateType).map((plateType) => (
            <NumericField
              key={plateType}
              label={plateType}
              startAdornment="$"
              endAdornment="/pound"
              min={0}
              numberOfDecimals={2}
              padWithZeros
              value={userSpecifiedPlatePrices[plateType]}
              onSubmit={(newValue) => void setUserSpecifiedPlatePrice(plateType, newValue)}
            />
          ))}
          {includeHangerPricesInEstimate &&
            (Object.keys(userSpecifiedHangerPrices).length > 0 ||
              Object.keys(userSpecifiedCustomHangerPrices).length > 0) && <Typography variant="h6">Hangers</Typography>}
          {includeHangerPricesInEstimate &&
            naturalSortByProperty(Object.keys(userSpecifiedHangerPrices), (hanger) => hanger).map((hanger) => (
              <NumericField
                key={`standard_hanger_${hanger}`}
                label={hanger}
                startAdornment="$"
                endAdornment="/unit"
                min={0}
                numberOfDecimals={2}
                padWithZeros
                value={userSpecifiedHangerPrices[hanger]}
                onSubmit={(newValue) => setUserSpecifiedHangerPrice(hanger, newValue)}
              />
            ))}
          {includeHangerPricesInEstimate &&
            naturalSortByProperty(Object.keys(userSpecifiedCustomHangerPrices), (hanger) => hanger).map((hanger) => (
              <NumericField
                key={`custom_hanger_${hanger}`}
                label={hanger}
                startAdornment="$"
                endAdornment="/unit"
                min={0}
                numberOfDecimals={2}
                padWithZeros
                value={userSpecifiedCustomHangerPrices[hanger]}
                onSubmit={(newValue) => setUserSpecifiedCustomHangerPrice(hanger, newValue)}
              />
            ))}
        </>
      )}
    </Stack>
  );
}
